import { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Wallet, FileText, ArrowUpRight, ArrowDownRight, Scale, Zap, BookOpen, Receipt, CreditCard } from 'lucide-react';
import { cn } from '../../../shared/lib/utils';
import { apiFetch } from '../../../shared/lib/api';
import { LoadingState } from '../../../shared/ui/design-system';
import { useIsMobile } from '../../../shared/hooks/useMediaQuery';
import PullToRefresh from '../../../shared/ui/PullToRefresh';

const fmt = (n: number) => n.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : fmt(n);

interface DashboardHomeProps { user: any; }

export default function DashboardHome({ user }: DashboardHomeProps) {
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [recentJEs, setRecentJEs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [kpiRes, jeRes] = await Promise.all([
          apiFetch('/api/financial-statements/dashboard-kpis'),
          apiFetch('/api/journal-entries'),
        ]);
        setData(await kpiRes.json());
        const jes = await jeRes.json();
        setRecentJEs((jes || []).slice(0, 6));
      } catch { /* fallback to empty */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <LoadingState />
  );

  const kpi = data?.kpi || {};
  const trend = data?.monthlyTrend || [];
  const engine = data?.engineStats || {};
  const maxTrend = Math.max(...trend.map((t: any) => Math.max(t.revenue, t.expenses)), 1);

  const cards = [
    { label: 'Total Revenue', value: kpi.totalRevenue || 0, icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', sub: `${kpi.netMargin || 0}% net margin` },
    { label: 'Net Income', value: kpi.netIncome || 0, icon: kpi.netIncome >= 0 ? ArrowUpRight : ArrowDownRight, color: kpi.netIncome >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400', bg: kpi.netIncome >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20', sub: 'Revenue − Expenses' },
    { label: 'Cash & Bank', value: kpi.cash || 0, icon: Wallet, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', sub: 'Checking + Savings' },
    { label: 'Accounts Receivable', value: kpi.ar || 0, icon: FileText, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', sub: 'Outstanding from customers' },
    { label: 'Accounts Payable', value: kpi.ap || 0, icon: CreditCard, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', sub: 'Owed to vendors' },
    { label: 'Total Assets', value: kpi.totalAssets || 0, icon: Scale, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20', sub: `Ratio: ${kpi.currentRatio || 0}x` },
  ];

  const refreshData = async () => {
    try {
      const [kpiRes, jeRes] = await Promise.all([
        apiFetch('/api/financial-statements/dashboard-kpis'),
        apiFetch('/api/journal-entries'),
      ]);
      setData(await kpiRes.json());
      const jes = await jeRes.json();
      setRecentJEs((jes || []).slice(0, 6));
    } catch { /* fallback */ }
  };

  return (
    <PullToRefresh onRefresh={refreshData} className="flex-1 overflow-auto custom-scrollbar p-3 sm:p-6 min-w-0 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Welcome back, {user?.name?.split(' ')[0] || 'User'}!</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Financial overview from your General Ledger</p>
      </div>

      {/* KPI Cards */}
      {isMobile ? (
        /* Mobile: scrollable compact stats */
        <div className="space-y-2">
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            {cards.slice(0, 4).map(c => (
              <div key={c.label} className="flex items-center gap-1.5 shrink-0">
                <c.icon className={cn("w-4 h-4", c.color)} />
                <span className={cn("text-sm font-black", c.color)}>${fmtK(c.value)}</span>
                <span className="text-[9px] text-slate-400">{c.label.split(' ').slice(-1)[0]}</span>
              </div>
            ))}
          </div>
          {/* Second row for remaining KPIs */}
          <div className="grid grid-cols-2 gap-2">
            {cards.map(c => (
              <div key={c.label} className={cn("border rounded-xl p-3 transition-all", c.bg)}>
                <div className="flex items-center gap-1.5 mb-1">
                  <c.icon className={cn("w-3.5 h-3.5", c.color)} />
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest truncate">{c.label}</span>
                </div>
                <p className={cn("text-base font-black", c.color)}>${fmtK(c.value)}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Desktop: grid cards */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {cards.map(c => (
            <div key={c.label} className={cn("border-2 rounded-2xl p-4 transition-all hover:shadow-md", c.bg)}>
              <div className="flex items-center justify-between mb-2">
                <c.icon className={cn("w-5 h-5", c.color)} />
              </div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{c.label}</p>
              <p className={cn("text-lg font-black mt-0.5", c.color)}>${fmtK(c.value)}</p>
              <p className="text-[9px] text-slate-400 mt-0.5">{c.sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Trend Chart */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900/40 border-2 border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 sm:mb-4">Revenue vs Expenses — Last 6 Months</h3>
          {trend.length > 0 ? (
            <div className="flex items-end gap-3 h-[160px]">
              {trend.map((t: any, i: number) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: 120 }}>
                    <div className="w-[35%] bg-emerald-500 rounded-t-md transition-all" style={{ height: `${(t.revenue / maxTrend) * 100}%`, minHeight: t.revenue > 0 ? 4 : 0 }}
                      title={`Revenue: $${fmt(t.revenue)}`} />
                    <div className="w-[35%] bg-rose-400 rounded-t-md transition-all" style={{ height: `${(t.expenses / maxTrend) * 100}%`, minHeight: t.expenses > 0 ? 4 : 0 }}
                      title={`Expenses: $${fmt(t.expenses)}`} />
                  </div>
                  <span className="text-[9px] text-slate-400 font-bold">{t.month}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-10">No data yet — post journal entries to see trends</p>
          )}
          <div className="flex items-center justify-center gap-4 mt-3 pt-2 border-t border-slate-200 dark:border-slate-800">
            <span className="flex items-center gap-1.5 text-[9px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Revenue</span>
            <span className="flex items-center gap-1.5 text-[9px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400" /> Expenses</span>
          </div>
        </div>

        {/* Engine Stats */}
        <div className="bg-white dark:bg-slate-900/40 border-2 border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex flex-col">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Accounting Engine</h3>
          <div className="space-y-3 flex-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-500" /> Auto Journal Entries</span>
              <span className="text-sm font-black text-amber-600 dark:text-amber-400">{engine.autoJECount || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-blue-500" /> Total Posted JEs</span>
              <span className="text-sm font-black text-blue-600 dark:text-blue-400">{engine.totalJECount || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5 text-emerald-500" /> Automation Rate</span>
              <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{engine.automationRate || 0}%</span>
            </div>
            {/* Progress bar */}
            <div className="mt-2">
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                <div className="bg-gradient-to-r from-amber-500 to-emerald-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(engine.automationRate || 0, 100)}%` }} />
              </div>
              <p className="text-[9px] text-slate-400 mt-1 text-center">
                {engine.automationRate > 0 ? 'Engine is active — transactions auto-post to GL' : 'Post invoices & payments to activate engine'}
              </p>
            </div>
          </div>

          {/* Quick insight */}
          <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800">
            <div className="bg-primary/5 dark:bg-primary/10 rounded-xl p-3 border border-primary/10">
              <div className="flex items-center gap-1.5 text-primary mb-1">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-[9px] font-black uppercase tracking-widest">How It Works</span>
              </div>
              <p className="text-[10px] text-slate-600 dark:text-slate-400 leading-relaxed">
                Invoice → Auto JE (DR: AR, CR: Revenue) → Payment → Auto JE (DR: Cash, CR: AR) → Trial Balance → Financial Statements
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Journal Entries */}
      <div className="bg-white dark:bg-slate-900/40 border-2 border-slate-200 dark:border-slate-800 rounded-2xl p-5">
        <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Recent Journal Entries</h3>
        {recentJEs.length > 0 ? (
          <div className="space-y-2">
            {recentJEs.map((je: any) => (
              <div key={je.id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                    je.description?.startsWith('[AUTO:') ? "bg-amber-500/10" : "bg-blue-500/10")}>
                    {je.description?.startsWith('[AUTO:') ? <Zap className="w-4 h-4 text-amber-500" /> : <BookOpen className="w-4 h-4 text-blue-500" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{je.entryNumber} — {je.description?.replace(/\[AUTO:\w+\]\s*/, '')}</p>
                    <p className="text-[10px] text-slate-400">{new Date(je.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {je.status}</p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-xs font-black text-slate-800 dark:text-slate-200">${fmt(je.totalDebit)}</p>
                  <span className={cn("text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full",
                    je.status === 'Posted' ? "bg-emerald-500/10 text-emerald-600" : je.status === 'Voided' ? "bg-rose-500/10 text-rose-500" : "bg-slate-100 text-slate-500 dark:bg-slate-800"
                  )}>{je.status}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">No journal entries yet — create invoices and payments to get started</p>
        )}
      </div>
    </PullToRefresh>
  );
}
